import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Light mode input styles
const inputStyle = {
  width: '100%', padding: '14px 18px', fontFamily: 'var(--sans)', fontSize: '14px', fontWeight: 300,
  background: '#fff', border: '1px solid #e8e3dc', color: '#2a2725', borderRadius: '10px',
  outline: 'none', transition: 'border-color .3s',
};

// Light mode label styles
const labelStyle = {
  fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.15em', textTransform: 'uppercase',
  color: '#8c8782', marginBottom: '8px', display: 'block',
};

const AuthPage = ({ onAuth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const demoData = location.state?.demoData || null;

  const [isLogin, setIsLogin] = useState(searchParams.get('mode') === 'login');
  const [view, setView] = useState('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  // Check if arriving with a reset token or verification token in URL
  useState(() => {
    const token = searchParams.get('reset');
    if (token) {
      setResetToken(token);
      setView('reset');
    }
    const verifyToken = searchParams.get('verify');
    if (verifyToken) {
      axios.get(`${API}/auth/verify-email?token=${verifyToken}`, { withCredentials: true })
        .then(() => { setSuccess('Email verified! You can now log in.'); setView('login'); })
        .catch(() => { setError('Invalid or expired verification link.'); });
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const body = isLogin ? { email, password } : { email, password, name };
      const res = await axios.post(`${API}${endpoint}`, body, { withCredentials: true });
      if (onAuth) onAuth(res.data.user);
      if (demoData && demoData.html) {
        try {
          const siteRes = await axios.post(`${API}/sites`, {
            business_name: demoData.business_name || 'My Studio',
            business_type: demoData.business_type || 'photographer',
            style: demoData.style || 'romantic',
            notes: demoData.notes || '',
            html: demoData.html,
          }, { withCredentials: true });
          navigate(`/builder/${siteRes.data.site.id}`);
          return;
        } catch { /* fallback to dashboard */ }
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess('If an account exists with that email, you\'ll receive a reset link shortly. Check your inbox.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token: resetToken, password }, { withCredentials: true });
      if (onAuth) {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        onAuth(me.data.user);
      }
      setSuccess(res.data.message);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong.');
    }
    setLoading(false);
  };

  return (
    <div className="builder-v2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#faf8f5', color: '#2a2725' }}>
      <div style={{ width: '100%', maxWidth: '420px', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '48px' }}>
          <a href="/" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: '#1F1D1B', textDecoration: 'none', letterSpacing: '-.02em' }}>
            golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em>
          </a>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(42,39,37,0.35)' }}>by Uncut Gems</span>
        </div>

        {/* ── Forgot Password ── */}
        {view === 'forgot' && (
          <>
            <button onClick={() => { setView('auth'); setIsLogin(true); setError(''); setSuccess(''); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#8c8782', fontFamily: 'var(--sans)', fontSize: '10px', letterSpacing: '.08em', marginBottom: '24px' }}>
              <ArrowLeft size={12} /> BACK TO SIGN IN
            </button>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: '36px', marginBottom: '8px', color: '#2a2725' }}>Reset your password.</h1>
            <p style={{ color: '#8c8782', fontSize: '14px', marginBottom: '40px' }}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleForgotPassword}>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); setSuccess(''); }} placeholder="jane@photography.com" required style={inputStyle} name="email" autoComplete="email" data-testid="forgot-email-input" />
              </div>
              {error && <p style={{ color: '#c47272', fontSize: '13px', marginBottom: '16px' }}>{error}</p>}
              {success && <p style={{ color: '#5ba89d', fontSize: '13px', marginBottom: '16px' }}>{success}</p>}
              <button type="submit" className="btn-primary" disabled={loading} style={{ width: '100%', textAlign: 'center' }} data-testid="forgot-submit">
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}

        {/* ── Reset Password (from link) ── */}
        {view === 'reset' && (
          <>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: '36px', marginBottom: '8px', color: '#2a2725' }}>Choose a new password.</h1>
            <p style={{ color: '#8c8782', fontSize: '14px', marginBottom: '40px' }}>
              Enter your new password below.
            </p>
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setError(''); }} placeholder="At least 6 characters" required minLength={6} style={{ ...inputStyle, paddingRight: '44px' }} name="password" autoComplete="new-password" data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#8c8782' }}>
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              {error && <p style={{ color: '#c47272', fontSize: '13px', marginBottom: '16px' }}>{error}</p>}
              {success && <p style={{ color: '#5ba89d', fontSize: '13px', marginBottom: '16px' }}>{success}</p>}
              <button type="submit" className="btn-primary" disabled={loading} style={{ width: '100%', textAlign: 'center' }} data-testid="reset-submit">
                {loading ? 'Updating...' : 'Set New Password'}
              </button>
            </form>
          </>
        )}

        {/* ── Login / Signup ── */}
        {view === 'auth' && (
          <>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: '36px', marginBottom: '8px', color: '#2a2725' }}>
              {isLogin ? 'Welcome back.' : 'Create your account.'}
            </h1>
            <p style={{ color: '#8c8782', fontSize: '14px', marginBottom: '40px' }}>
              {isLogin ? 'Sign in to continue building.' : demoData ? 'Sign up to save your site.' : 'Start building your dream website.'}
            </p>

            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Your Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" style={inputStyle} data-testid="auth-name-input" name="name" autoComplete="name" />
                </div>
              )}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Email</label>
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder="jane@photography.com" required style={inputStyle} data-testid="auth-email-input" name="email" autoComplete="email" />
              </div>
              <div style={{ marginBottom: '8px' }}>
                <label style={labelStyle}>Password</label>
                <div style={{ position: 'relative' }}>
                  <input type={showPw ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setError(''); }} placeholder={isLogin ? 'Your password' : 'At least 6 characters'} required minLength={6} style={{ ...inputStyle, paddingRight: '44px' }} data-testid="auth-password-input" name="password" autoComplete={isLogin ? 'current-password' : 'new-password'} />
                  <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#8c8782' }}>
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div style={{ textAlign: 'right', marginBottom: '16px' }}>
                  <button type="button" onClick={() => { setView('forgot'); setError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8c8782', fontSize: '12px', fontFamily: 'var(--sans)' }} data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
              )}

              {!isLogin && <div style={{ marginBottom: '16px' }} />}

              {error && <p style={{ color: '#c47272', fontSize: '13px', marginBottom: '16px' }} data-testid="auth-error">{error}</p>}

              <button type="submit" className="btn-primary" disabled={loading} style={{ width: '100%', textAlign: 'center', marginBottom: '24px', opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }} data-testid="auth-submit-button">
                {loading ? 'One moment...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: '13px', color: '#8c8782' }}>
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button onClick={() => { setIsLogin(!isLogin); setError(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C9A84C', fontSize: '13px' }} data-testid="auth-toggle">
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
